import * as React from 'react';
import * as ReactDOM from 'react-dom/client';
import { CssBaseline } from '@mui/material';
import RtlProvider from '@mui/system/RtlProvider';
import { CacheProvider } from '@emotion/react';
import Layout from './shared/layout';
import { BrowserRouter } from 'react-router-dom';
import { AuthProvider } from './services/common/auth-context';
import { MediaUploadLimitsProvider } from './services/common/media-upload-limits-context';
import { createRTLCache } from './shared/rtl-cache';
import { useTranslation } from 'react-i18next';
import { register } from './serviceWorkerRegistration';
import './shared/i18n';
import { LanguageCode } from './helper-types/general/consts/language-code';

const Root: React.FC = () => {
  const { i18n } = useTranslation();
  const [direction, setDirection] = React.useState<'ltr' | 'rtl'>(
    (localStorage.getItem('direction') as 'ltr' | 'rtl') || 'rtl'
  );

  React.useEffect(() => {
    localStorage.setItem('direction', direction);
    document.body.dir = direction;
    document.documentElement.lang = direction === 'rtl' ? LanguageCode.AR : LanguageCode.EN;
    i18n.changeLanguage(direction === 'rtl' ? LanguageCode.AR : LanguageCode.EN).catch((err) => {
    });
  }, [direction, i18n]);

  // The login/register pages switch language themselves (i18n.changeLanguage, without going through
  // Layout's setDirection), so follow i18n here as the single source of truth — otherwise the
  // RTL/LTR Emotion cache below stays on the old direction and labels/adornments stay mirrored
  // wrongly until a refresh.
  React.useEffect(() => {
    const onLanguageChanged = (language: string) => {
      setDirection(language.startsWith(LanguageCode.AR.split('-')[0]) ? 'rtl' : 'ltr');
    };
    i18n.on('languageChanged', onLanguageChanged);
    return () => {
      i18n.off('languageChanged', onLanguageChanged);
    };
  }, [i18n]);

  const cache = React.useMemo(() => createRTLCache(direction === 'rtl'), [direction]);

  return (
    <CacheProvider value={cache}>
      {/* MUI's theme `direction` only drives CSS (via stylis-plugin-rtl above); components
          that pick their icon/behavior in JS based on direction (TablePagination's
          first/last arrows, Tabs scroll buttons, Slider, Menu, etc.) read a separate
          RtlProvider context that ThemeProvider does NOT set up automatically — without
          this, those icons stay LTR-oriented even though the layout around them mirrors. */}
      <RtlProvider value={direction === 'rtl'}>
        <CssBaseline />
        <AuthProvider>
          <MediaUploadLimitsProvider>
            <BrowserRouter>
              <Layout setDirection={setDirection} />
            </BrowserRouter>
          </MediaUploadLimitsProvider>
        </AuthProvider>
      </RtlProvider>
    </CacheProvider>
  );
};

const root = ReactDOM.createRoot(document.getElementById('root')!);
root.render(
  <React.StrictMode>
    <Root />
  </React.StrictMode>
);

// Register service worker for PWA
if (import.meta.env.PROD) {
  register({
    onSuccess: (registration: ServiceWorkerRegistration) => {
    },
    onUpdate: (registration: ServiceWorkerRegistration) => {
      // You could prompt the user to update here if desired
    },
  });
}